// src/components/Admin/Leads/SalesLeadDetailPanel.jsx
//
// Read-only lead detail view for the Sales Call Leads page. Modeled visually
// on the specialist-facing LeadDetail (selyst-frontend-next-app), scoped down
// to what a salesperson needs to prep a call: no purchase/reject/notes UI.
// Phone/email are always the backend-masked strings — render as-is, never
// attempt to unmask.
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Separator } from '../../ui/separator';
import { Mail, Phone, MapPin, Globe, ShieldCheck, Coins } from 'lucide-react';

export default function SalesLeadDetailPanel({ lead, loading }) {
  if (loading) {
    return <div className="flex items-center justify-center h-full text-muted-foreground">Loading lead…</div>;
  }

  if (!lead) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Select a lead from the list to see details.
      </div>
    );
  }

  return (
    <Card className="h-full overflow-y-auto">
      <CardHeader className="space-y-1">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-xl">{lead.buyer_first_name || 'Buyer'}</CardTitle>
          <div className="flex items-center gap-2 shrink-0">
            {lead.remote_service && (
              <Badge variant="outline" className="gap-1">
                <Globe className="h-3 w-3" /> Remote
              </Badge>
            )}
            <Badge variant="secondary" className="gap-1">
              <Coins className="h-3 w-3" />
              {lead.price_coins != null ? `${lead.price_coins} coins` : 'N/A'}
            </Badge>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">{lead.category_name}</p>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 text-sm">
          <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span>{lead.full_address}</span>
        </div>

        <Separator />

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span>{lead.masked_phone || 'Not available'}</span>
            {lead.is_buyer_phone_verified && <ShieldCheck className="h-4 w-4 text-green-600" aria-label="Verified" />}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span>{lead.masked_email || 'Not available'}</span>
            {lead.is_buyer_email_verified && <ShieldCheck className="h-4 w-4 text-green-600" aria-label="Verified" />}
          </div>
          <p className="text-xs text-muted-foreground">Contact details are masked for this view.</p>
        </div>

        {lead.qna_details?.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Requirement details</h4>
              {lead.qna_details.map((qna, i) => (
                <div key={i} className="text-sm">
                  <p className="text-muted-foreground">{qna.question}</p>
                  <p className="font-medium">{String(qna.answer ?? '')}</p>
                </div>
              ))}
            </div>
          </>
        )}

        <Separator />

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
          <span>{lead.purchase_count} of {lead.max_purchases} sellers connected</span>
          <span>Posted: {lead.created_at ? new Date(lead.created_at).toLocaleString() : 'N/A'}</span>
        </div>
      </CardContent>
    </Card>
  );
}
